import _isEqual from 'lodash.isequal'
import { formatDate, formatTime } from '@smarttransit/common'
import {
  getBeaconSettings,
  updateBeaconSettings
} from '@/services/st-settings-service'
import { addAlert } from '@/utilities/helpers'

const MIN_PURCHASES_PER_WEEK_ITEMS = [1, 2, 3, 4, 5].map((n) => ({ text: String(n), value: n }))
const EVALUATION_WEEKS_ITEMS = [1, 2, 3, 4, 6, 8, 12].map((n) => ({ text: String(n), value: n }))

function toWholeNumberOrNull (value) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
    return null
  }
  return parsed
}

export default {
  name: 'advanced-beacon-settings',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      apiInProgress: false,
      restrictionEnabled: false,
      minPurchasesPerWeek: 3,
      evaluationWeeks: 2,
      originalRestrictionEnabled: false,
      originalMinPurchasesPerWeek: 3,
      originalEvaluationWeeks: 2,
      dateUpdatedLabel: '',
      minPurchasesPerWeekItems: MIN_PURCHASES_PER_WEEK_ITEMS,
      evaluationWeeksItems: EVALUATION_WEEKS_ITEMS
    }
  },
  computed: {
    isCurrentSettingsEdited () {
      return !_isEqual({
        restrictionEnabled: this.restrictionEnabled,
        minPurchasesPerWeek: this.minPurchasesPerWeek,
        evaluationWeeks: this.evaluationWeeks
      }, {
        restrictionEnabled: this.originalRestrictionEnabled,
        minPurchasesPerWeek: this.originalMinPurchasesPerWeek,
        evaluationWeeks: this.originalEvaluationWeeks
      })
    }
  },
  mounted () {
    this.loadSettings()
  },
  methods: {
    applySettings (setting) {
      const frequency = setting && setting.portalRegistrationTicketFrequency
      if (frequency && frequency !== false && typeof frequency === 'object') {
        this.restrictionEnabled = true
        this.minPurchasesPerWeek = Number(frequency.minPurchasesPerWeek) || 3
        this.evaluationWeeks = Number(frequency.evaluationWeeks) || 2
      } else {
        this.restrictionEnabled = false
        this.minPurchasesPerWeek = 3
        this.evaluationWeeks = 2
      }

      this.originalRestrictionEnabled = this.restrictionEnabled
      this.originalMinPurchasesPerWeek = this.minPurchasesPerWeek
      this.originalEvaluationWeeks = this.evaluationWeeks

      this.dateUpdatedLabel = setting && setting.dateUpdated
        ? `${formatDate(setting.dateUpdated)}, ${formatTime(setting.dateUpdated)}`
        : ''
    },
    validateCurrentSettings () {
      if (this.restrictionEnabled) {
        const min = toWholeNumberOrNull(this.minPurchasesPerWeek)
        const weeks = toWholeNumberOrNull(this.evaluationWeeks)

        if (min === null || min < 1 || min > 5) {
          alert('Tickets/top-ups per week must be a whole number between 1 and 5')
          return false
        }

        if (weeks === null || weeks < 1 || weeks > 12) {
          alert('Evaluation weeks must be a whole number between 1 and 12')
          return false
        }
      }

      return true
    },
    async loadSettings () {
      this.apiInProgress = true
      try {
        const settings = await getBeaconSettings()
        this.applySettings(settings || {})
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    },
    cancelEdit () {
      if (!this.isCurrentSettingsEdited) {
        return
      }
      if (confirm('Discard unsaved beacon settings changes?')) {
        this.restrictionEnabled = this.originalRestrictionEnabled
        this.minPurchasesPerWeek = this.originalMinPurchasesPerWeek
        this.evaluationWeeks = this.originalEvaluationWeeks
      }
    },
    async saveSettings () {
      if (!this.validateCurrentSettings()) {
        return
      }

      this.apiInProgress = true
      try {
        const portalRegistrationTicketFrequency = this.restrictionEnabled
          ? {
            minPurchasesPerWeek: toWholeNumberOrNull(this.minPurchasesPerWeek),
            evaluationWeeks: toWholeNumberOrNull(this.evaluationWeeks)
          }
          : false

        const updatedSettings = await updateBeaconSettings({ portalRegistrationTicketFrequency })
        this.applySettings(updatedSettings || {})
        addAlert({ message: 'Successfully updated beacon settings', type: 'success' })
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    }
  }
}
